import React from 'react';
import { X, TrendingUp, Users, DollarSign, Briefcase, Activity, PieChart } from 'lucide-react';
import { Modal, LoadingSpinner, Badge, Card, CardHeader, CardTitle, CardContent } from '@/components/ui';

interface ProgramAnalytics {
  program: {
    id: string;
    name: string;
    isActive: boolean;
  };
  capacity: {
    totalTeams: number;
    activeTeams: number;
    totalAllocatedTW: number;
    totalResources: number;
    totalAnnualCost: number;
  };
  resources: {
    total: number;
    byType: Record<string, number>;
    byLevel: Record<string, number>;
  };
  epics: {
    primary: {
      count: number;
      statusBreakdown: Record<string, number>;
      byCategoryFromIS: Record<string, number>;
      totalFeatures: number;
    };
    contributing: {
      count: number;
    };
  };
  features: {
    total: number;
    statusBreakdown: Record<string, number>;
  };
  teams: Array<{
    teamId: string;
    teamName: string;
    resourceCount: number;
    annualCost: number;
    costProfile: string;
  }>;
  trends: {
    allocations: Array<{
      period: string;
      totalTW: number;
      epicCount: number;
    }>;
  };
}

interface ProgramAnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  analytics: ProgramAnalytics | null;
  isLoading: boolean;
}

const ProgramAnalyticsModal: React.FC<ProgramAnalyticsModalProps> = ({
  isOpen,
  onClose,
  analytics,
  isLoading,
}) => {
  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      PLANNED: 'info',
      IN_PROGRESS: 'warning',
      COMPLETED: 'success',
      ON_HOLD: 'secondary',
      CANCELLED: 'danger',
      SHOVEL_READY: 'success',
      IN_REFINEMENT: 'warning',
      NOT_READY: 'secondary',
    };
    return statusColors[status] || 'secondary';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl">
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-[var(--border)]">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text)] flex items-center gap-2">
              <Activity className="w-6 h-6 text-accent-blue" />
              Program Analytics
            </h2>
            {analytics && (
              <p className="text-sm text-[var(--muted)] mt-1">{analytics.program.name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--surface-hover)] transition-colors"
            aria-label="Close analytics modal"
          >
            <X className="w-5 h-5 text-[var(--muted)]" />
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : analytics ? (
          <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Teams */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[var(--muted)] uppercase">Teams</p>
                      <p className="text-2xl font-bold text-[var(--text)] mt-1">
                        {analytics.capacity.totalTeams}
                      </p>
                      <p className="text-xs text-[var(--muted)] mt-1">
                        {analytics.capacity.activeTeams} active
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-accent-blue/10">
                      <Users className="w-6 h-6 text-accent-blue" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Total Resources */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[var(--muted)] uppercase">Resources</p>
                      <p className="text-2xl font-bold text-[var(--text)] mt-1">
                        {analytics.capacity.totalResources}
                      </p>
                      <p className="text-xs text-[var(--muted)] mt-1">team members</p>
                    </div>
                    <div className="p-3 rounded-lg bg-category-business/10">
                      <Users className="w-6 h-6 text-category-business" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Allocated Team Weeks */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[var(--muted)] uppercase">Allocated TW</p>
                      <p className="text-2xl font-bold text-[var(--text)] mt-1">
                        {analytics.capacity.totalAllocatedTW}
                      </p>
                      <p className="text-xs text-[var(--muted)] mt-1">team weeks</p>
                    </div>
                    <div className="p-3 rounded-lg bg-category-tech/10">
                      <Briefcase className="w-6 h-6 text-category-tech" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Annual Cost */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[var(--muted)] uppercase">Annual Cost</p>
                      <p className="text-2xl font-bold text-[var(--text)] mt-1">
                        {formatCurrency(analytics.capacity.totalAnnualCost)}
                      </p>
                      <p className="text-xs text-[var(--muted)] mt-1">estimated</p>
                    </div>
                    <div className="p-3 rounded-lg bg-status-warning/10">
                      <DollarSign className="w-6 h-6 text-status-warning" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* EPICs & Features */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Primary EPICs */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Primary EPICs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[var(--muted)]">Total EPICs</span>
                      <span className="text-lg font-semibold text-[var(--text)]">
                        {analytics.epics.primary.count}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[var(--muted)]">Contributing EPICs</span>
                      <span className="text-lg font-semibold text-[var(--text)]">
                        {analytics.epics.contributing.count}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[var(--muted)]">Total Features</span>
                      <span className="text-lg font-semibold text-[var(--text)]">
                        {analytics.epics.primary.totalFeatures}
                      </span>
                    </div>

                    {/* Status Breakdown */}
                    {Object.keys(analytics.epics.primary.statusBreakdown).length > 0 && (
                      <div className="pt-3 border-t border-[var(--border)]">
                        <p className="text-xs text-[var(--muted)] uppercase mb-2">By Status</p>
                        <div className="space-y-2">
                          {Object.entries(analytics.epics.primary.statusBreakdown).map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between">
                              <Badge variant={getStatusColor(status) as any} size="sm">
                                {status.replace(/_/g, ' ')}
                              </Badge>
                              <span className="text-sm font-medium text-[var(--text)]">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Category Breakdown */}
                    {Object.keys(analytics.epics.primary.byCategoryFromIS).length > 0 && (
                      <div className="pt-3 border-t border-[var(--border)]">
                        <p className="text-xs text-[var(--muted)] uppercase mb-2">By IS Category</p>
                        <div className="space-y-2">
                          {Object.entries(analytics.epics.primary.byCategoryFromIS).map(([category, count]) => (
                            <div key={category} className="flex items-center justify-between">
                              <Badge
                                variant={
                                  category === 'BUSINESS' ? 'business' : category === 'TECH' ? 'tech' : 'support'
                                }
                                size="sm"
                              >
                                {category}
                              </Badge>
                              <span className="text-sm font-medium text-[var(--text)]">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Feature Readiness */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Feature Readiness</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[var(--muted)]">Total Features</span>
                      <span className="text-lg font-semibold text-[var(--text)]">
                        {analytics.features.total}
                      </span>
                    </div>

                    {/* Status Breakdown */}
                    {Object.keys(analytics.features.statusBreakdown).length > 0 && (
                      <div className="pt-3 border-t border-[var(--border)]">
                        <p className="text-xs text-[var(--muted)] uppercase mb-2">By Readiness</p>
                        <div className="space-y-2">
                          {Object.entries(analytics.features.statusBreakdown).map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between">
                              <Badge variant={getStatusColor(status) as any} size="sm">
                                {status.replace(/_/g, ' ')}
                              </Badge>
                              <span className="text-sm font-medium text-[var(--text)]">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Resources Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* By Type */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Resources by Type
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(analytics.resources.byType).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0">
                        <span className="text-sm text-[var(--text)]">{type.replace(/_/g, ' ')}</span>
                        <span className="text-sm font-semibold text-[var(--text)]">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* By Level */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Resources by Level
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {Object.entries(analytics.resources.byLevel)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([level, count]) => (
                        <div key={level} className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0">
                          <span className="text-sm text-[var(--text)]">{level}</span>
                          <span className="text-sm font-semibold text-[var(--text)]">{count}</span>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Team Cost Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Team Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[var(--border)]">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--muted)] uppercase">
                          Team
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--muted)] uppercase">
                          Resources
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[var(--muted)] uppercase">
                          Cost Profile
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-[var(--muted)] uppercase">
                          Annual Cost
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...analytics.teams]
                        .sort((a, b) => b.annualCost - a.annualCost)
                        .map((team) => (
                          <tr key={team.teamId} className="border-b border-[var(--border)] last:border-0">
                            <td className="py-3 px-3 text-sm text-[var(--text)]">{team.teamName}</td>
                            <td className="py-3 px-3 text-sm text-[var(--text)]">{team.resourceCount}</td>
                            <td className="py-3 px-3">
                              <Badge variant="secondary" size="sm">
                                {team.costProfile}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-sm font-semibold text-[var(--text)] text-right">
                              {formatCurrency(team.annualCost)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[var(--border)] font-semibold">
                        <td className="py-3 px-3 text-sm text-[var(--text)]">Total</td>
                        <td className="py-3 px-3 text-sm text-[var(--text)]">
                          {analytics.teams.reduce((sum, t) => sum + t.resourceCount, 0)}
                        </td>
                        <td className="py-3 px-3"></td>
                        <td className="py-3 px-3 text-sm text-[var(--text)] text-right">
                          {formatCurrency(analytics.teams.reduce((sum, t) => sum + t.annualCost, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Allocation Trends */}
            {analytics.trends.allocations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Allocation Trends (Last 6 Months)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.trends.allocations.map((trend) => (
                      <div
                        key={trend.period}
                        className="flex items-center justify-between py-3 px-4 rounded-lg bg-[var(--surface-hover)]"
                      >
                        <div>
                          <p className="text-sm font-semibold text-[var(--text)]">{trend.period}</p>
                          <p className="text-xs text-[var(--muted)] mt-1">
                            {trend.epicCount} EPICs
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-accent-blue">{trend.totalTW}</p>
                          <p className="text-xs text-[var(--muted)]">Team Weeks</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-[var(--muted)]">No analytics data available</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ProgramAnalyticsModal;
